import type { FC } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

import { Button, Tooltip } from '@/components/ui';
import { formatCost } from '@/lib/utils';

interface DraftCreatorFooterProps {
  canCreate: boolean;
  missingRequirements: string[];
  estimatedCost: string;
  isBusy: boolean;
  selectedCount: number;
  tldValue: string;
  onCreate: () => Promise<void>;
}

const statusText = (selectedCount: number, tldValue: string): string => {
  if (selectedCount === 0) return 'Select at least 1 leads list';
  if (!tldValue) return 'Select a TLD';
  return 'Ready to create drafts';
};

/** Shared sticky footer: validation status + the "Create N Drafts" CTA. */
export const DraftCreatorFooter: FC<DraftCreatorFooterProps> = ({
  canCreate,
  missingRequirements,
  estimatedCost,
  isBusy,
  selectedCount,
  tldValue,
  onCreate,
}) => (
  <footer className="sticky bottom-0 z-10 shrink-0 border-t border-border bg-card/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-card/80">
    <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
      <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
        {canCreate ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
        ) : (
          <AlertCircle className="h-4 w-4 shrink-0" />
        )}
        <span className="min-w-0 truncate">{statusText(selectedCount, tldValue)}</span>
        {selectedCount > 1 && canCreate ? (
          <span className="shrink-0 font-semibold text-destructive">
            Total Est: {formatCost((Number(estimatedCost) || 0) * selectedCount)}
          </span>
        ) : null}
      </div>
      <Tooltip
        side="top"
        content={
          !canCreate && missingRequirements.length > 0 ? (
            <div className="space-y-1.5 text-left leading-relaxed">
              <span className="font-medium">Missing:</span>
              <ul className="list-inside list-disc space-y-0.5 pl-1">
                {missingRequirements.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          ) : null
        }
      >
        <Button
          onClick={onCreate}
          loading={isBusy}
          disabled={!canCreate || isBusy}
          className="min-w-[180px] shadow-md"
        >
          {isBusy ? 'Processing…' : `Create ${selectedCount} Drafts`}
        </Button>
      </Tooltip>
    </div>
  </footer>
);

export default DraftCreatorFooter;
